import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { 
  ArrowLeft, Layers, Download, RefreshCw, Sparkles, AlertCircle,
  ChevronDown, ChevronUp, Edit3, Check, X, Save, Loader2
} from 'lucide-react';
import { 
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, 
  Radar, ResponsiveContainer
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// The 15 categories matching the AI Assessment
const SCORECARD_SECTIONS = {
  section1: {
    name: 'Central Control',
    color: '#2563EB',
    categories: {
      leadership: { name: 'Leadership & General Management', items: 12 },
      riskFinancing: { name: 'Risk Financing', items: 12 },
      hrPractices: { name: 'HR Practices', items: 8 },
      incidentPrevention: { name: 'Incident Prevention Practices', items: 8 }
    }
  },
  section2: {
    name: 'Gray Matter',
    color: '#7C3AED',
    categories: {
      enterpriseRisk: { name: 'Enterprise Risk Assessment', items: 8 },
      riskCommunication: { name: 'Risk Communication', items: 7 },
      metrics: { name: 'Metrics and Measures', items: 6 }
    }
  },
  section3: {
    name: 'Risk Improvement',
    color: '#EA580C',
    categories: {
      districtPrograms: { name: 'District Wide Programs', items: 11 },
      employeeSafety: { name: 'Employee Safety Programs', items: 13 },
      equity: { name: 'Equity & Violence Prevention', items: 11 },
      totalHealth: { name: 'Total Health', items: 7 }
    }
  },
  section4: {
    name: 'Ancillary Operations',
    color: '#059669',
    categories: {
      discreteServices: { name: 'Discrete Services', items: 9 },
      supplyChain: { name: 'Supply Chain & Construction', items: 8 },
      injuryManagement: { name: 'Injury Management', items: 8 },
      claimsManagement: { name: 'Claims Management', items: 8 }
    }
  }
};

const getScoreColor = (score) => {
  if (score >= 80) return { text: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200' };
  if (score >= 60) return { text: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' };
  if (score >= 40) return { text: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' };
  return { text: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' };
};

const getScoreLabel = (score) => {
  if (score >= 80) return 'Excellent';
  if (score >= 60) return 'Good';
  if (score >= 40) return 'Fair';
  if (score >= 20) return 'Developing';
  return 'Needs Attention';
};

function ScoreDots({ score, editable, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          disabled={!editable}
          onClick={() => editable && onChange(n)}
          className={`w-4 h-4 rounded-sm transition-all ${
            n <= score ? 'bg-blue-500' : 'bg-slate-200'
          } ${editable ? 'cursor-pointer hover:ring-2 hover:ring-blue-300 hover:scale-110' : ''}`}
          data-testid={`score-dot-${n}`}
        />
      ))}
    </div>
  );
}

function CategoryCard({ categoryKey, category, scores, sectionColor, onScoreChange }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});

  const categoryScores = scores[categoryKey] || {};
  const items = Object.entries(categoryScores);
  
  const avgScore = items.length > 0 
    ? Math.round((items.reduce((acc, [_, score]) => acc + score, 0) / items.length / 5) * 100)
    : 0;
  
  const scoreStyle = getScoreColor(avgScore);

  const handleLocalScoreChange = (itemName, newScore) => {
    setPendingChanges(prev => ({ ...prev, [itemName]: newScore }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const changes = Object.entries(pendingChanges);
      for (const [itemName, newScore] of changes) {
        await axios.put(`${API_URL}/api/scorecard/update-scores`, {
          category_key: categoryKey,
          item_name: itemName,
          score: newScore
        }, { withCredentials: true });
      }
      onScoreChange(categoryKey, pendingChanges);
      setPendingChanges({});
      setEditing(false);
    } catch (error) {
      console.error('Failed to save scores:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setPendingChanges({});
    setEditing(false);
  };

  const getDisplayScore = (itemName, originalScore) => {
    return pendingChanges[itemName] !== undefined ? pendingChanges[itemName] : originalScore;
  };

  const hasChanges = Object.keys(pendingChanges).length > 0;

  return (
    <div className={`border ${scoreStyle.border} rounded-lg overflow-hidden`} data-testid={`category-card-${categoryKey}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full px-4 py-3 ${scoreStyle.bg} flex items-center justify-between hover:opacity-90 transition-all`}
        data-testid={`expand-${categoryKey}`}
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-2 h-8 rounded-full"
            style={{ backgroundColor: sectionColor }}
          />
          <div className="text-left">
            <h4 className="font-medium text-slate-900">{category.name}</h4>
            <p className="text-xs text-slate-500">{items.length} of {category.items} items scored</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xl font-outfit font-bold ${scoreStyle.text}`}>
            {avgScore}%
          </span>
          {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
        </div>
      </button>
      
      {expanded && items.length > 0 && (
        <div className="bg-white">
          {/* Edit toolbar */}
          <div className="px-4 py-2 border-b border-slate-100 flex items-center justify-between bg-slate-50">
            {editing ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSave}
                  disabled={saving || !hasChanges}
                  className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white text-xs font-medium rounded-md hover:bg-emerald-700 disabled:opacity-50 transition-all"
                  data-testid={`save-scores-${categoryKey}`}
                >
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-1 px-3 py-1.5 text-slate-600 text-xs font-medium rounded-md hover:bg-slate-200 transition-all"
                  data-testid={`cancel-edit-${categoryKey}`}
                >
                  <X className="w-3 h-3" /> Cancel
                </button>
                {hasChanges && (
                  <span className="text-xs text-amber-600 font-medium">
                    {Object.keys(pendingChanges).length} unsaved change(s)
                  </span>
                )}
              </div>
            ) : (
              <span className="text-xs text-slate-500">Click scores to adjust</span>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); setEditing(!editing); if (editing) handleCancel(); }}
              className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                editing ? 'bg-slate-200 text-slate-700' : 'text-blue-600 hover:bg-blue-50'
              }`}
              data-testid={`edit-toggle-${categoryKey}`}
            >
              <Edit3 className="w-3 h-3" />
              {editing ? 'Editing' : 'Edit Scores'}
            </button>
          </div>

          <div className="p-4 space-y-1.5 max-h-72 overflow-y-auto">
            {items.map(([itemName, score]) => {
              const displayScore = getDisplayScore(itemName, score);
              const isChanged = pendingChanges[itemName] !== undefined;
              return (
                <div 
                  key={itemName} 
                  className={`flex items-center justify-between py-1.5 px-2 rounded-md transition-all ${
                    isChanged ? 'bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-slate-50'
                  }`}
                  data-testid={`score-item-${itemName}`}
                >
                  <span className="text-sm text-slate-700 truncate flex-1 mr-4">{itemName}</span>
                  <div className="flex items-center gap-2">
                    <ScoreDots 
                      score={displayScore} 
                      editable={editing} 
                      onChange={(newScore) => handleLocalScoreChange(itemName, newScore)} 
                    />
                    <span className={`text-sm font-medium w-8 ${isChanged ? 'text-blue-600' : 'text-slate-600'}`}>
                      {displayScore}/5
                    </span>
                    {isChanged && <Check className="w-3.5 h-3.5 text-blue-500" />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {expanded && items.length === 0 && (
        <div className="p-4 bg-white text-center text-slate-500 text-sm">
          No scores yet. Complete AI Assessment to populate.
        </div>
      )}
    </div>
  );
}

export default function BalancedScorecardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [scorecardData, setScorecardData] = useState(null);
  const [scores, setScores] = useState({});
  const [noScorecard, setNoScorecard] = useState(false);

  useEffect(() => {
    const loadScorecard = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/scorecard/latest`, {
          withCredentials: true
        });
        
        if (response.data.status === 'completed' && response.data.scorecard) {
          setScorecardData(response.data.scorecard);
          setScores(response.data.scorecard.scores || {});
        } else if (response.data.status === 'in_progress') {
          setScores(response.data.scores || {});
        } else {
          setNoScorecard(true);
        }
      } catch (error) {
        console.error('Failed to load scorecard:', error);
        setNoScorecard(true);
      } finally {
        setLoading(false);
      }
    };
    loadScorecard();
  }, []);

  const handleScoreChange = useCallback((categoryKey, changes) => {
    setScores(prev => {
      const updated = { ...prev };
      updated[categoryKey] = { ...updated[categoryKey] };
      Object.entries(changes).forEach(([itemName, newScore]) => {
        updated[categoryKey][itemName] = newScore;
      });
      return updated;
    });
  }, []);

  const calculateOverallScore = () => {
    let totalScore = 0;
    let totalItems = 0;
    Object.values(scores).forEach(categoryScores => {
      if (typeof categoryScores === 'object') {
        Object.values(categoryScores).forEach(score => {
          if (typeof score === 'number') {
            totalScore += score;
            totalItems++;
          }
        });
      }
    });
    return totalItems > 0 ? Math.round((totalScore / (totalItems * 5)) * 100) : 0;
  };

  const getSectionScore = (sectionKey) => {
    const section = SCORECARD_SECTIONS[sectionKey];
    let totalScore = 0;
    let totalItems = 0;
    Object.keys(section.categories).forEach(catKey => {
      const categoryScores = scores[catKey];
      if (categoryScores && typeof categoryScores === 'object') {
        Object.values(categoryScores).forEach(score => {
          if (typeof score === 'number') {
            totalScore += score;
            totalItems++;
          }
        });
      }
    });
    return totalItems > 0 ? Math.round((totalScore / (totalItems * 5)) * 100) : 0;
  };

  const radarData = Object.entries(SCORECARD_SECTIONS).map(([key, section]) => ({
    section: section.name,
    score: getSectionScore(key),
    fullMark: 100
  }));

  const overallScore = calculateOverallScore();
  const scoreStyle = getScoreColor(overallScore);
  const totalItemsScored = Object.values(scores).reduce((acc, cat) => 
    acc + (typeof cat === 'object' ? Object.keys(cat).length : 0), 0
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-500">Loading scorecard...</div>
      </div>
    );
  }

  if (noScorecard || totalItemsScored === 0) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="max-w-6xl mx-auto flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-slate-100 rounded-lg transition-all">
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Layers className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="font-outfit text-xl font-bold text-slate-900">Balanced Scorecard</h1>
                <p className="text-sm text-slate-500">15-Category Risk Assessment</p>
              </div>
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto p-6">
          <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-blue-50 flex items-center justify-center">
              <Sparkles className="w-10 h-10 text-blue-500" />
            </div>
            <h2 className="font-outfit text-2xl font-bold text-slate-900 mb-3">No Scorecard Yet</h2>
            <p className="text-slate-600 mb-8 max-w-md mx-auto">
              Complete the AI Assessment to generate your Balanced Scorecard with scores across all 15 categories.
            </p>
            <button
              onClick={() => navigate('/ai-assessment')}
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all inline-flex items-center gap-2"
              data-testid="start-ai-assessment-btn"
            >
              <Sparkles className="w-5 h-5" />
              Start AI Assessment
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="p-2 hover:bg-slate-100 rounded-lg transition-all">
                <ArrowLeft className="w-5 h-5 text-slate-500" />
              </Link>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Layers className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h1 className="font-outfit text-xl font-bold text-slate-900">Balanced Scorecard</h1>
                  <p className="text-sm text-slate-500">
                    {scorecardData?.created_at ? `Generated ${new Date(scorecardData.created_at).toLocaleDateString()}` : 'AI-Generated Assessment'}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => navigate('/ai-assessment')}
                className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg font-medium transition-all flex items-center gap-2"
                data-testid="retake-assessment-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Retake Assessment
              </button>
              <button
                onClick={async () => {
                  try {
                    const res = await fetch(`${API_URL}/api/report/export`, { credentials: 'include' });
                    if (!res.ok) throw new Error('Export failed');
                    const blob = await res.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `ERIC_Assessment_Report_${new Date().toISOString().slice(0,10)}.pdf`;
                    link.click();
                    window.URL.revokeObjectURL(url);
                  } catch (err) {
                    console.error('Export failed:', err);
                    alert('Failed to export report. Please ensure you have a completed scorecard.');
                  }
                }}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium transition-all flex items-center gap-2"
                data-testid="export-pdf-btn"
              >
                <Download className="w-4 h-4" />
                Export PDF
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Overall Score Banner */}
        <div className={`${scoreStyle.bg} border ${scoreStyle.border} rounded-xl p-6 mb-8`} data-testid="overall-score">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Overall Risk Intelligence Score</p>
              <div className="flex items-baseline gap-3">
                <span className={`text-5xl font-outfit font-bold ${scoreStyle.text}`}>{overallScore}%</span>
                <span className="text-lg font-medium text-slate-600">{getScoreLabel(overallScore)}</span>
              </div>
              <p className="text-sm text-slate-500 mt-1">
                {totalItemsScored} items scored across 15 categories
              </p>
              <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                <Edit3 className="w-3 h-3" /> Expand categories to manually adjust scores
              </p>
            </div>
            <div className="w-full lg:w-64 h-48">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#E2E8F0" />
                  <PolarAngleAxis dataKey="section" tick={{ fontSize: 11, fill: '#64748B' }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Radar
                    name="Score"
                    dataKey="score"
                    stroke="#2563EB"
                    fill="#2563EB"
                    fillOpacity={0.3}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {Object.entries(SCORECARD_SECTIONS).map(([sectionKey, section]) => {
            const sectionScore = getSectionScore(sectionKey);
            const sectionStyle = getScoreColor(sectionScore);
            
            return (
              <div key={sectionKey} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div 
                  className="px-6 py-4 border-b border-slate-200 flex items-center justify-between"
                  style={{ borderLeftWidth: '4px', borderLeftColor: section.color }}
                >
                  <div>
                    <h2 className="font-outfit text-lg font-semibold text-slate-900">{section.name}</h2>
                    <p className="text-sm text-slate-500">{Object.keys(section.categories).length} categories</p>
                  </div>
                  <div className={`px-4 py-2 rounded-lg ${sectionStyle.bg}`}>
                    <span className={`text-2xl font-outfit font-bold ${sectionStyle.text}`}>{sectionScore}%</span>
                  </div>
                </div>
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(section.categories).map(([catKey, category]) => (
                    <CategoryCard 
                      key={catKey}
                      categoryKey={catKey}
                      category={category}
                      scores={scores}
                      sectionColor={section.color}
                      onScoreChange={handleScoreChange}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Recommendations */}
        <div className="mt-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
            <div>
              <h3 className="font-outfit text-lg font-semibold text-amber-900 mb-2">Focus Areas</h3>
              <p className="text-amber-800 text-sm">
                Based on your scores, consider prioritizing improvements in categories scoring below 60%. 
                The AI Assistant can provide specific recommendations for each area.
              </p>
              <button
                onClick={() => navigate('/chat')}
                className="mt-3 text-amber-700 font-medium hover:text-amber-900 text-sm flex items-center gap-1"
                data-testid="ask-ai-recommendations"
              >
                Ask AI for recommendations
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
